import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://dietdubacherla.in/diet_backend";
const TOTAL_SEMESTER_LEAVES = 20;

export default function StudentDashboard() {
  const [notifications, setNotifications] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [leaveDays, setLeaveDays] = useState("");
  const [reason, setReason] = useState("");
  const [myLeaves, setMyLeaves] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [profile, setProfile] = useState(null);
  const [message, setMessage] = useState("");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const logout = () => {
    localStorage.removeItem("user");
    navigate("/student-login");
  };

  const fetchMyLeaves = () => {
    if (!user?.college_id) return;

    axios
      .post(`${API_BASE}/get_my_leaves.php`, {
        college_id: user.college_id,
        
      })
      .then((res) => setMyLeaves(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMyLeaves([]));
  };

  const fetchProfile = () => {
    if (!user?.college_id) return;

    axios
      .post(`${API_BASE}/student_profile.php`, {
        action: "get",
        deecet_id: user.college_id,
      })
      .then((res) => {
        if (res.data?.status === "success") {
          setProfile(res.data.profile);
          if (res.data.profile?.batch && !user?.batch) fetchMaterials(res.data.profile.batch);
        }
      })
      .catch(() => setProfile(null));
  };

  const fetchMaterials = (batch) => {
    const batchParam = batch ? `?batch=${encodeURIComponent(batch)}` : "";
    axios
      .get(`${API_BASE}/get_materials.php${batchParam}`)
      .then((res) => setMaterials(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMaterials([]));
  };

  useEffect(() => {
    if (!user) {
      navigate("/student-login");
      return;
    }

    axios.get(`${API_BASE}/get_notifications.php`).then((res) => setNotifications(Array.isArray(res.data) ? res.data : []));
    fetchMaterials(user.batch);
    axios.get(`${API_BASE}/get_timetables.php?audience=student`).then((res) => setTimetables(Array.isArray(res.data) ? res.data : []));
    fetchMyLeaves();
    fetchProfile();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const remainingLeaves = useMemo(() => {
    const latestWithBalance = myLeaves.find((leave) => leave.remaining_leaves !== undefined && leave.remaining_leaves !== null);
    return latestWithBalance ? Number(latestWithBalance.remaining_leaves) : TOTAL_SEMESTER_LEAVES;
  }, [myLeaves]);

  const approvedDays = useMemo(
    () => myLeaves.reduce((total, leave) => (leave.status === "Approved" ? total + Number(leave.leave_days || 0) : total), 0),
    [myLeaves]
  );

  const pendingLeaves = myLeaves.filter((leave) => leave.status === "Pending").length;

  const applyLeave = (e) => {
    e.preventDefault();
    setMessage("");

    const requestedDays = Number(leaveDays);

    if (!fromDate || !toDate || !requestedDays || requestedDays < 1 || !reason.trim()) {
      setMessage("Please enter from date, to date, number of days and reason.");
      return;
    }
     if (fromDate > toDate) {
    setMessage("From date cannot be greater than To date");
    return;
  }
   const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (fromDate < today) {
    setMessage("From date cannot be in the past");
    return;
  }


    if (requestedDays > remainingLeaves) {
      setMessage(`You have only ${remainingLeaves} leave days remaining this semester.`);
      return;
    }

    axios
      .post(`${API_BASE}/apply_leave.php`, {
        college_id: user.college_id,
        name: profile?.name || user.name,
        
        from_date: fromDate,
        to_date: toDate,
        leave_days: requestedDays,
        reason: reason.trim(),
      })
      .then((res) => {
        if (res.data.status === "success") {
          setFromDate("");
          setToDate("");
          setLeaveDays("");
          setReason("");
          setMessage("Leave application submitted for admin approval.");
          fetchMyLeaves();
        } else {
          setMessage(res.data.message || "Unable to submit leave application.");
        }
      })
      .catch(() => setMessage("Server error while submitting leave application."));
  };

  return (
    <div style={styles.page}>
      <header style={{ ...styles.hero, ...(isMobile ? styles.heroMobile : {}) }}>
        <div>
          <p style={styles.eyebrow}>Student Portal</p>
          <h1 style={styles.title}>Welcome, {profile?.name || user?.name || "Student"}</h1>
          <p style={styles.subtitle}>ID {user?.college_id || "-"}</p>
        </div>
        <div style={styles.heroActions}>
          <button style={styles.secondaryButton} onClick={() => navigate("/student-profile")}>Profile</button>
          <button onClick={logout} style={styles.primaryButton}>Logout</button>
        </div>
      </header>

      <section style={styles.metricsGrid}>
        <MetricCard label="Leaves Remaining" value={remainingLeaves} caption={`out of ${TOTAL_SEMESTER_LEAVES}`} tone="#991b1b" />
        <MetricCard label="Approved Days" value={approvedDays} caption="used this semester" tone="#166534" />
        <MetricCard label="Pending Requests" value={pendingLeaves} caption="waiting for admin" tone="#b45309" />
        <MetricCard label="Materials" value={materials.length} caption="files and quizzes" tone="#1d4ed8" />
      </section>

      <main style={{ ...styles.contentGrid, ...(isMobile ? styles.contentGridMobile : {}) }}>
        <section style={styles.leftColumn}>
          <Panel title="Profile" action={<button style={styles.linkButton} onClick={() => navigate("/student-profile")}>Update</button>}>
            {profile ? (
              <div style={styles.profileBlock}>
                {profile.photo_url ? <img src={profile.photo_url} alt="Student" style={styles.avatar} /> : <div style={styles.avatarEmpty}>ST</div>}
                <div>
                  <h3 style={styles.profileName}>{profile.surname} {profile.name}</h3>
                  <p style={styles.muted}>Admission No: {profile.admission_no || "Not added"}</p>
                  <p style={styles.muted}>Batch: {profile.batch || "Not added"} | Medium: {profile.medium || "Not added"}</p>
                </div>
              </div>
            ) : (
              <EmptyText text="Complete your profile so admin can verify your details." />
            )}
          </Panel>

          <Panel title="Apply Leave">
            <form onSubmit={applyLeave}>
              <div style={styles.formGrid}>
                <Field label="From" type="date" value={fromDate} onChange={setFromDate} />
                <Field label="To" type="date" value={toDate} onChange={setToDate} />
                <Field label="Days" type="number" min="1" max={remainingLeaves} placeholder="No. of days" value={leaveDays} onChange={setLeaveDays} />
              </div>
              <label style={styles.fieldLabel}>
                Reason
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" style={{ ...styles.input, minHeight: "86px", resize: "vertical" }} />
              </label>
              {message && <p style={styles.notice}>{message}</p>}
              <button type="submit" style={styles.submitButton}>Submit Leave Request</button>
            </form>
          </Panel>

          <Panel title="My Leaves">
            <div style={styles.listStack}>
              {myLeaves.map((leave) => (
                <div key={leave.id} style={styles.listItem}>
                  <div>
                    <strong>{leave.reason || "Leave request"}</strong>
                    <p style={styles.muted}>{leave.from_date} to {leave.to_date} | {leave.leave_days || 1} day(s)</p>
                  </div>
                  <StatusBadge status={leave.status} />
                </div>
              ))}
              {myLeaves.length === 0 && <EmptyText text="No leave requests submitted yet." />}
            </div>
          </Panel>
        </section>

        <section style={styles.rightColumn}>
          <Panel title="Notifications">
            <div style={styles.listStackScrollable}>
              {notifications.map((item) => (
                <div key={item.id} style={styles.listItemPlain}>
                  <strong>{item.title}</strong>
                  <p style={styles.muted}>{item.message}</p>
                </div>
              ))}
              {notifications.length === 0 && <EmptyText text="No notifications found." />}
            </div>
          </Panel>

          <Panel title="Study Materials & Quizzes">
            <ResourceGrid items={materials} emptyText="No materials uploaded yet." render={(item) => (
              <ResourceCard
                key={item.id}
                title={item.subject}
                meta={item.type === "quiz" ? `Quiz for batch ${item.batch}` : item.uploaded_by}
                href={item.type === "quiz" ? item.quiz_link : `${API_BASE}/uploads/${item.file_name}`}
                label={item.type === "quiz" ? "Open Quiz" : "Download"}
              />
            )} />
          </Panel>

          <Panel title="Timetable">
            <ResourceGrid items={timetables} emptyText="No timetable uploaded yet." render={(item) => (
              <ResourceCard key={item.id} title={item.title} meta={item.audience} href={item.file_url} label="View" />
            )} />
          </Panel>
        </section>
      </main>
    </div>
  );
}

function MetricCard({ label, value, caption, tone }) {
  return (
    <div style={styles.metricCard}>
      <span style={{ ...styles.metricBar, background: tone }} />
      <p style={styles.metricLabel}>{label}</p>
      <strong style={styles.metricValue}>{value}</strong>
      <span style={styles.metricCaption}>{caption}</span>
    </div>
  );
}

function Panel({ title, action, children }) {
  return (
    <section style={styles.panel}>
      <div style={styles.panelHeader}>
        <h2 style={styles.panelTitle}>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, ...props }) {
  return (
    <label style={styles.fieldLabel}>
      {label}
      <input value={value} onChange={(e) => onChange(e.target.value)} style={styles.input} {...props} />
    </label>
  );
}

function ResourceGrid({ items, emptyText, render }) {
  if (!items.length) return <EmptyText text={emptyText} />;
  return <div style={styles.resourceGrid}>{items.map(render)}</div>;
}

function ResourceCard({ title, meta, href, label }) {
  return (
    <article style={styles.resourceCard}>
      <div>
        <strong>{title || "Untitled"}</strong>
        <p style={styles.muted}>{meta || "-"}</p>
      </div>
      <a href={href} target="_blank" rel="noreferrer" style={styles.resourceLink}>{label}</a>
    </article>
  );
}

function StatusBadge({ status }) {
  const tone = status === "Approved" ? styles.approved : status === "Rejected" ? styles.rejected : styles.pending;
  return <span style={{ ...styles.statusBadge, ...tone }}>{status}</span>;
}

function EmptyText({ text }) {
  return <p style={styles.emptyText}>{text}</p>;
}

const styles = {
  page: { minHeight: "100vh", background: "#f4f6f8", color: "#1f2937", padding: "24px", fontFamily: "Inter, Arial, sans-serif", boxSizing: "border-box" },
  hero: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "18px", background: "#7f1d1d", color: "#fff", borderRadius: "8px", padding: "24px", marginBottom: "16px" },
  heroMobile: { alignItems: "stretch", flexDirection: "column" },
  eyebrow: { margin: "0 0 6px", color: "#fecaca", fontSize: "12px", fontWeight: 800, textTransform: "uppercase" },
  title: { margin: 0, fontSize: "28px", lineHeight: 1.15 },
  subtitle: { margin: "8px 0 0", color: "#fee2e2" },
  heroActions: { display: "flex", gap: "10px", flexWrap: "wrap" },
  primaryButton: { background: "#fff", color: "#7f1d1d", border: "none", borderRadius: "6px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  secondaryButton: { background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "6px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  metricsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" },
  metricCard: { position: "relative", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px", overflow: "hidden" },
  metricBar: { position: "absolute", left: 0, top: 0, bottom: 0, width: "4px" },
  metricLabel: { margin: 0, color: "#6b7280", fontSize: "13px", fontWeight: 700 },
  metricValue: { display: "block", margin: "8px 0 4px", fontSize: "30px", color: "#111827" },
  metricCaption: { color: "#6b7280", fontSize: "12px" },
  contentGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(320px, 0.9fr)", gap: "16px", alignItems: "start" },
  contentGridMobile: { gridTemplateColumns: "1fr" },
  leftColumn: { display: "grid", gap: "16px" },
  rightColumn: { display: "grid", gap: "16px" },
  panel: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" },
  panelHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "12px" },
  panelTitle: { margin: 0, color: "#111827", fontSize: "18px" },
  linkButton: { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: "6px", padding: "7px 10px", fontWeight: 800, cursor: "pointer" },
  profileBlock: { display: "flex", alignItems: "center", gap: "14px" },
  avatar: { width: "78px", height: "78px", borderRadius: "8px", objectFit: "cover", border: "1px solid #e5e7eb" },
  avatarEmpty: { width: "78px", height: "78px", borderRadius: "8px", display: "grid", placeItems: "center", background: "#fee2e2", color: "#991b1b", fontWeight: 900 },
  profileName: { margin: "0 0 6px", color: "#111827" },
  muted: { margin: "4px 0 0", color: "#6b7280", fontSize: "13px" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: "10px" },
  fieldLabel: { display: "grid", gap: "6px", color: "#374151", fontSize: "13px", fontWeight: 800, marginBottom: "10px" },
  input: { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: "6px", padding: "10px", fontSize: "14px", background: "#fff" },
  submitButton: { width: "100%", background: "#991b1b", color: "#fff", border: "none", borderRadius: "6px", padding: "11px", fontWeight: 900, cursor: "pointer" },
  notice: { margin: "8px 0", color: "#991b1b", fontWeight: 800 },
  listStack: { display: "grid", gap: "10px", maxHeight: "300px", overflowY: "auto", paddingRight: "4px" },
  listStackScrollable: { display: "grid", gap: "10px", maxHeight: "260px", overflowY: "auto" },
  listItem: { display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb" },
  listItemPlain: { padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb" },
  statusBadge: { flex: "0 0 auto", padding: "6px 9px", borderRadius: "999px", fontSize: "12px", fontWeight: 900 },
  approved: { color: "#166534", background: "#dcfce7" },
  rejected: { color: "#991b1b", background: "#fee2e2" },
  pending: { color: "#92400e", background: "#fef3c7" },
  resourceGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "10px", maxHeight: "320px", overflowY: "auto", paddingRight: "4px" },
  resourceCard: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb" },
  resourceLink: { flex: "0 0 auto", textDecoration: "none", color: "#fff", background: "#7f1d1d", borderRadius: "6px", padding: "7px 9px", fontSize: "13px", fontWeight: 900 },
  emptyText: { margin: 0, padding: "14px", color: "#6b7280", border: "1px dashed #d1d5db", borderRadius: "8px", background: "#f9fafb", fontWeight: 700 },
};
